// Technology Tree for Canntycoon
// Five branches: A=Environmental, B=Pest/Disease, C=Genetics, D=Business, E=Post-Harvest

export const TECH_BRANCHES = {
  A: { id: 'A', name: 'Environmental', color: '#58a6ff', icon: '🌡️' },
  B: { id: 'B', name: 'Pest & Disease', color: '#f85149', icon: '🔬' },
  C: { id: 'C', name: 'Genetics', color: '#3fb950', icon: '🧬' },
  D: { id: 'D', name: 'Business', color: '#d29922', icon: '📊' },
  E: { id: 'E', name: 'Post-Harvest', icon: '💎', color: '#f0c040' },
};

export const TECH_NODES = {
  // ── Branch A: Environmental ─────────────────────────────────────────────
  'A1': {
    id: 'A1', branch: 'A', tier: 1,
    name: 'Basic Automation',
    description: 'Install basic HVAC controllers. Reduces environmental drift by 30%. Unlocks humidity controls.',
    cost: 2000, rp: 50,
    requires: [],
    minStage: 1,
    effects: { driftReduction: 0.30, unlocksHumidityControl: true },
    position: { x: 0, y: 0 },
  },
  'A2': {
    id: 'A2', branch: 'A', tier: 2,
    name: 'CO₂ Controller',
    description: 'Install dedicated CO₂ injection system. Enables CO₂ targeting up to 1500ppm. Significant yield boost.',
    cost: 3500, rp: 80,
    requires: ['A1'],
    minStage: 1,
    effects: { enablesCO2Control: true, co2YieldBonus: 0.15 },
    position: { x: 0, y: 1 },
  },
  'A3': {
    id: 'A3', branch: 'A', tier: 3,
    name: 'Full HVAC Automation',
    description: 'Full environmental automation. Drift reduced 70%. Labor requirement drops 25%. Equipment lasts longer.',
    cost: 8000, rp: 150,
    requires: ['A2'],
    minStage: 2,
    effects: { driftReduction: 0.70, laborReduction: 0.25, equipDegradeReduction: 0.20 },
    position: { x: 0, y: 2 },
  },
  'A4': {
    id: 'A4', branch: 'A', tier: 4,
    name: 'AI Environment Optimizer',
    description: 'Machine learning continuously tunes the environment to strain optima. Near-perfect VPD targeting.',
    cost: 25000, rp: 400,
    requires: ['A3'],
    minStage: 3,
    effects: { driftReduction: 0.92, autoOptimize: true },
    position: { x: 0, y: 3 },
  },

  // ── Branch B: Pest & Disease ─────────────────────────────────────────────
  'B1': {
    id: 'B1', branch: 'B', tier: 1,
    name: 'Pest Detection Cameras',
    description: 'High-res cameras with IR. Pest outbreaks detected early — damage reduced 50%.',
    cost: 1500, rp: 40,
    requires: [],
    minStage: 1,
    effects: { pestDamageReduction: 0.50, revealsSaboteurs: true },
    position: { x: 1, y: 0 },
  },
  'B2': {
    id: 'B2', branch: 'B', tier: 2,
    name: 'IPM Protocol',
    description: 'Integrated Pest Management. Reduces pest outbreak probability by 40%. Compliance bonus.',
    cost: 1000, rp: 60,
    requires: ['B1'],
    minStage: 1,
    effects: { pestProbabilityReduction: 0.40, complianceBonus: 5 },
    position: { x: 1, y: 1 },
  },
  'B3': {
    id: 'B3', branch: 'B', tier: 3,
    name: 'UV Sterilization',
    description: 'UV-C air sterilization system. Mold probability reduced 60%. Requires good airflow.',
    cost: 3000, rp: 100,
    requires: ['B2'],
    minStage: 2,
    effects: { moldProbabilityReduction: 0.60 },
    position: { x: 1, y: 2 },
  },

  // ── Branch C: Genetics ───────────────────────────────────────────────────
  'C1': {
    id: 'C1', branch: 'C', tier: 1,
    name: 'Phenohunting Lab',
    description: 'Dedicated phenotype selection workspace. Unlocks phenohunting mechanic — improve strains over grows.',
    cost: 5000, rp: 120,
    requires: [],
    minStage: 2,
    effects: { unlocksPhenohunting: true },
    position: { x: 2, y: 0 },
  },
  'C2': {
    id: 'C2', branch: 'C', tier: 2,
    name: 'Genetic Stabilization',
    description: 'Lab protocols to reduce hermaphroditism risk across all strains by 50%.',
    cost: 4000, rp: 100,
    requires: ['C1'],
    minStage: 2,
    effects: { hermRiskReduction: 0.50 },
    position: { x: 2, y: 1 },
  },
  'C3': {
    id: 'C3', branch: 'C', tier: 3,
    name: 'Crossbreeding Module',
    description: 'Full hybridization lab. Combine strains to create proprietary genetics. High regulatory scrutiny.',
    cost: 15000, rp: 300,
    requires: ['C2'],
    minStage: 3,
    effects: { unlocksCrossbreeding: true },
    position: { x: 2, y: 2 },
  },

  // ── Branch D: Business ───────────────────────────────────────────────────
  'D1': {
    id: 'D1', branch: 'D', tier: 1,
    name: 'Compliance Software',
    description: 'Automated inventory tracking and compliance reporting. Fine risk -30%. Required for medical license.',
    cost: 2000, rp: 50,
    requires: [],
    minStage: 1,
    effects: { fineRiskReduction: 0.30, complianceBonus: 10 },
    position: { x: 3, y: 0 },
  },
  'D2': {
    id: 'D2', branch: 'D', tier: 2,
    name: 'Brand Builder',
    description: 'Marketing materials and brand identity. Reputation gain +25%. Unlocks retail sales channel.',
    cost: 3000, rp: 80,
    requires: ['D1'],
    minStage: 2,
    effects: { repGainBonus: 0.25, unlocksRetailChannel: true },
    position: { x: 3, y: 1 },
  },
  'D3': {
    id: 'D3', branch: 'D', tier: 3,
    name: 'CRM System',
    description: 'Customer relationship management. Unlocks medical contracts. Contract prices +15%.',
    cost: 5000, rp: 120,
    requires: ['D2'],
    minStage: 2,
    effects: { unlocksMedicalContracts: true, contractPriceBonus: 0.15 },
    position: { x: 3, y: 2 },
  },
  'D4': {
    id: 'D4', branch: 'D', tier: 4,
    name: 'Marketing Department',
    description: 'Full in-house marketing team. Brand value grows passively each week. Unlocks export partnerships.',
    cost: 10000, rp: 200,
    requires: ['D3'],
    minStage: 3,
    effects: { weeklyBrandGain: 2, unlocksExportContracts: true },
    position: { x: 3, y: 3 },
  },

  // ── Branch E: Post-Harvest ───────────────────────────────────────────────
  'E1': {
    id: 'E1', branch: 'E', tier: 1,
    name: 'Extraction Basics',
    description: 'Unlock rosin press extraction. Converts flower into concentrate.',
    cost: 5000, rp: 60,
    requires: [], minStage: 2,
    effects: { unlockExtraction: 'rosin' },
    position: { x: 4, y: 0 },
  },
  'E2': {
    id: 'E2', branch: 'E', tier: 2,
    name: 'Solvent Extraction',
    description: 'Unlock wax and shatter production via BHO process.',
    cost: 10000, rp: 120,
    requires: ['E1'], minStage: 2,
    effects: { unlockExtraction: 'solvent' },
    position: { x: 4, y: 1 },
  },
};

export const TECH_LIST = Object.values(TECH_NODES);
export const getTechNode = (id) => TECH_NODES[id];

// Research point costs (used to generate RP via scientist workers)
export const RP_PER_SCIENTIST_PER_WEEK = 15;
